import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  Input,
  DrawerHeader as ModalHeader,
  DrawerContent as ModalContent,
  RectangleSkeleton,
  Alert
} from '@sainsburys-tech/bolt'
import { isUkPostcode } from '@sainsburys-tech/bolt-validators'
import { getValue } from '../../utils/ConfigProvider'
import {
  pushEventThenTrack,
  trackAvailabilityModalOpen,
  trackAvailabilityModalSuccess
} from '../../helpers/TaggingSerializer'
import formatPostcode from '../../utils/formatPostcode'

import Template from './AvailabilityModal.template'
import * as Styled from './styles'

const AvailabilityModal = ({
  showLocalisationModal,
  localisationPostcode,
  partNumber,
  pageType,
  handleLocalisationModal,
  fetchUserLocalisation
}) => {
  const [postcode, setPostcode] = useState(localisationPostcode)
  const [errorMessage, setErrorMessage] = useState('')
  const [alert, setAlert] = useState('')
  const [loading, setLoading] = useState(false)
  const radius = getValue('features.availability.radius')
  const {
    localised,
    unlocalised,
    errorMessages: { emptyPostcodeMessage, invalidPostcodeMessage, failedCallMessage }
  } = Template

  useEffect(() => {
    if (showLocalisationModal) {
      setPostcode(formatPostcode(localisationPostcode))
      trackAvailabilityModalOpen(pageType)
    }
  }, [showLocalisationModal])

  // Removing any spacing
  const normalisedPostcode = postcode.trim().replace(/\s/g, '')

  const handleSubmit = e => {
    e.preventDefault()
    clearErrors()

    if (!normalisedPostcode) {
      setErrorMessage(emptyPostcodeMessage)
    } else if (!isUkPostcode(normalisedPostcode)) {
      setErrorMessage(invalidPostcodeMessage)
    } else if (normalisedPostcode.toUpperCase() === localisationPostcode.toUpperCase()) {
      // Prevent making a call if same postcode is submitted
      handleModalClose()
    } else {
      // Close modal if same postcode is submitted
      postcode.toUpperCase().trim() !== localisationPostcode.toUpperCase() ? localiseUser() : handleModalClose()
    }
  }

  const localiseUser = async () => {
    setLoading(true)
    const url = `/stores/api/orchestrator/v0/cis-locator/availability?maxDistance=${radius}&maxResults=1&skuQty=${partNumber}_1&postcode=${postcode.trim()}&channel=web_plp`
    fetch(url)
      .then(async response => {
        if (!response.ok) {
          return Promise.reject(response)
        }
        setLoading(false)
        // Callback to update postcode to state
        fetchUserLocalisation()
        handleModalClose()

        // Track success
        trackAvailabilityModalSuccess(normalisedPostcode, pageType)
      })
      .catch(error => {
        setLoading(false)
        setAlert(failedCallMessage)
        console.error(error)

        // Track failure
        pushEventThenTrack({
          eventInfo: {
            eventName: 'localisation_localisationFailure',
            eventAction: 'Localisation Failure'
          },
          attributes: {
            pageType: ['search', 'canned', 'sd'].includes(pageType) ? 'search' : 'plp',
            error: error.status,
            errorMessage: error.statusText
          }
        })
      })
  }

  const clearErrors = () => {
    errorMessage && setErrorMessage('')
    alert && setAlert('')
  }

  const handleModalClose = () => {
    clearErrors()
    handleLocalisationModal()
  }

  const content = () => {
    const data = localisationPostcode ? localised(localisationPostcode, radius) : unlocalised
    const { headerText, bodyText } = data

    return (
      <>
        <ModalHeader>{headerText}</ModalHeader>
        <ModalContent>
          <p>{bodyText}</p>
          {loading ? (
            <RectangleSkeleton height={20} />
          ) : (
            <>
              {alert && (
                <Alert kind='error' isCloseable={false}>
                  {alert}
                </Alert>
              )}
              <form onSubmit={handleSubmit} data-test='availability-modal-form'>
                <Input
                  name='postcode'
                  type='text'
                  data-test='postcode-input'
                  placeholder='Enter postcode'
                  aria-label='Enter postcode'
                  buttonOptions={{
                    text: 'Submit',
                    type: 'submit',
                    show: true,
                    kind: 'primary',
                    disabled: loading
                  }}
                  value={postcode}
                  onChange={e => setPostcode(e.target.value)}
                  error={errorMessage}
                  autoComplete='off'
                />
              </form>
            </>
          )}
        </ModalContent>
      </>
    )
  }

  return (
    <Styled.ExtendedModal
      padding
      show={showLocalisationModal}
      height='initial'
      onClose={() => handleModalClose()}
      data-test='availability-modal'>
      {content()}
    </Styled.ExtendedModal>
  )
}

AvailabilityModal.propTypes = {
  localisationPostcode: PropTypes.string,
  showLocalisationModal: PropTypes.bool,
  partNumber: PropTypes.string.isRequired,
  pageType: PropTypes.string.isRequired,
  handleLocalisationModal: PropTypes.func.isRequired,
  fetchUserLocalisation: PropTypes.func.isRequired
}

AvailabilityModal.defaultProps = {
  localisationPostcode: '',
  showLocalisationModal: false
}

export default AvailabilityModal
